/**
 * Bildschirm 2 von 3 — die Objektansicht (Spec 01, „Oberfläche", erweitert
 * um Spec 02a, „Bildschirm Objekt · der Rahmen für alle drei Arten", Spec
 * 02b, „Oberfläche · Kopfaktionen der Objektansicht", und Spec 03,
 * „Oberfläche": zwei weitere Arten — Mieter und Vertrag — teilen sich
 * denselben Rahmen, ohne einen sechsten Reiter (ADR 0011)).
 *
 * Sie hat **immer dieselben fünf Reiter**, unabhängig von der Objektart. Nur
 * *Stammdaten* unterscheidet sich je Art (`design-system`). Diese Route ist
 * der Container für den ganzen Bildschirm: Sie besitzt den Kopf
 * (`GET /entities/{id}`, artübergreifend, `features/objects`) **und** die
 * kind-eigene Stammdaten-Query (`GET /{art}/{id}`, `features/properties` /
 * `features/tenants` / `features/contracts`) — „zwei Aufrufe je Bildschirm"
 * (Spec 02a). Alle fünf Stammdaten-Queries werden unbedingt aufgerufen
 * (Regeln der Hooks) und über `enabled` nach `kind` geschaltet, sobald der
 * Kopf geladen ist. Dieselbe Schaltung trägt die Vertragsliste im Reiter
 * *Geld* an Grundstück, Gebäude, Einheit und Mieter (Spec 03, „Reiter Geld —
 * die Vertragsliste": „dieselbe Komponente an vier Objektarten").
 *
 * Zusammensetzung von `features/objects` (Baum-unabhängig: Kopf, Pfad,
 * Hinweis, Umhäng-Sheet), `features/properties` (Stammdaten, Anlegen-/
 * Duplizier-Sheets), `features/tenants` und `features/contracts` (Spec 03)
 * geschieht hier, weil Features einander nicht kennen
 * (`apps/web/CLAUDE.md`). `MoveSheet` liegt in `features/objects`, weil ihr
 * Auslöser hier im Kopf sitzt — dieselbe Begründung wie `building-add`/
 * `unit-add` (Spec 02b, „Entsteht neu") und wie das Zusammenspiel von
 * `ContractCreateSheet` und `TenantCreateSheet` unten: Ein Vertrag legt aus
 * sich heraus einen Mieter an, aber `features/contracts` kennt
 * `features/tenants` nicht — komponiert wird hier, `pendingTenant` ist der
 * Weg des neu angelegten Mieters zurück ins offene Vertragssheet.
 */

import { Link, useNavigate, useParams, useRouterState, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { ConfirmationStrip } from '@/components/ConfirmationStrip'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { ApiError } from '@/lib/api'
import { useMe } from '@/features/access/api'
import { AccessAddGrantSheet } from '@/features/access/AccessAddGrantSheet'
import { AccessSheet } from '@/features/access/AccessSheet'
import type { PersonSelection } from '@/features/access/PersonPicker'
import { useContractCharges, useOpenItems } from '@/features/billing/api'
import { ChargesList } from '@/features/billing/ChargesList'
import { OpenItemsList } from '@/features/billing/OpenItemsList'
import { TenantLedger } from '@/features/billing/TenantLedger'
import { ContractCreateSheet } from '@/features/contracts/ContractCreateSheet'
import { ContractList } from '@/features/contracts/ContractList'
import { ContractMasterData } from '@/features/contracts/ContractMasterData'
import { useContractDetail, useInfiniteContractsList } from '@/features/contracts/api'
import type { TenantSelection } from '@/features/contracts/TenantPicker'
import { DocumentList } from '@/features/documents/DocumentList'
import { useInfiniteDocumentsList } from '@/features/documents/api'
import { useEntityHead, useFlatEntities } from '@/features/objects/api'
import { MeterCreateSheet } from '@/features/energy/MeterSheet'
import { FolderMasterData } from '@/features/objects/FolderMasterData'
import { MoveSheet } from '@/features/objects/MoveSheet'
import { ObjectArchivedNotice } from '@/features/objects/ObjectArchivedNotice'
import { ObjectPath } from '@/features/objects/ObjectPath'
import { PrivateToggle } from '@/features/objects/PrivateToggle'
import { useBuildingDetail, usePropertyDetail, useUnitDetail } from '@/features/properties/api'
import { BuildingMasterData } from '@/features/properties/BuildingMasterData'
import { BuildingSheet } from '@/features/properties/BuildingSheet'
import { FIELD_CLASS } from '@/components/FormField'
import { PropertyMasterData } from '@/features/properties/PropertyMasterData'
import { PropertySheet } from '@/features/properties/PropertySheet'
import { UnitMasterData } from '@/features/properties/UnitMasterData'
import { UnitSheet } from '@/features/properties/UnitSheet'
import { useInfiniteTicketsList } from '@/features/tasks/api'
import { TaskList } from '@/features/tasks/TaskList'
import { useInfiniteDueDatesList } from '@/features/schedule/api'
import { DueDateList } from '@/features/schedule/DueDateList'
import { TenantCreateSheet } from '@/features/tenants/TenantCreateSheet'
import { TenantMasterData } from '@/features/tenants/TenantMasterData'
import { useTenantDetail } from '@/features/tenants/api'

const FACETS = [
  { id: 'dokumente', label: 'Dokumente' },
  { id: 'aufgaben', label: 'Aufgaben' },
  { id: 'termine', label: 'Termine' },
  { id: 'stammdaten', label: 'Stammdaten' },
] as const

export type TabId = (typeof FACETS)[number]['id'] | 'geld'

/** Statuston der Server-`LabeledValue`. Dieselben Tokens, die `DueDateList`
 * und `OpenItemsList` verwenden — iOS färbt über `ObjectStatusTint` gleich.
 * Vorher stand hier fest `text-muted`, wodurch „Überfällig" grau war, während
 * dieselbe Überfälligkeit zwei Zeilen tiefer rot erschien. */
function statusToneClass(value: string): string {
  if (value === 'overdue') return 'text-status-overdue'
  if (value === 'none') return 'text-status-paid'
  return 'text-muted'
}

export function ObjectDetail() {
  const navigate = useNavigate({ from: '/objekt/$entityId' })
  const { entityId } = useParams({ from: '/objekt/$entityId' })
  // Der aktive Reiter steht in der URL, nicht im Komponentenzustand: Ein Link
  // auf „Geld eines Gebäudes" muss teilbar sein und ein Neuladen darf nicht auf
  // Stammdaten zurückspringen.
  const { tab } = useSearch({ from: '/objekt/$entityId' })
  // Trägt nur etwas, wenn diese Ansicht das Ziel eines gerade abgeschlossenen
  // Archivierens ist (`lib/archived-notice.ts`) — sonst `undefined`.
  const archivedNotice = useRouterState({ select: (state) => state.location.state.archivedNotice })
  const head = useEntityHead(entityId)
  const kind = head.data?.kind.value
  const isOverview = tab === undefined || tab === 'geld'
  const isMasterDataFacet = tab === 'stammdaten'
  const [isPropertyDuplicateSheetOpen, setIsPropertyDuplicateSheetOpen] = useState(false)
  const [isBuildingDuplicateSheetOpen, setIsBuildingDuplicateSheetOpen] = useState(false)
  const [isUnitDuplicateSheetOpen, setIsUnitDuplicateSheetOpen] = useState(false)

  // Alle fünf unbedingt aufgerufen (Regeln der Hooks) und per `enabled`
  // geschaltet, sobald `kind` feststeht — nur eine lädt tatsächlich.
  const property = usePropertyDetail(entityId, { enabled: (isMasterDataFacet || isPropertyDuplicateSheetOpen) && kind === 'property' })
  const building = useBuildingDetail(entityId, { enabled: (isMasterDataFacet || isBuildingDuplicateSheetOpen) && kind === 'building' })
  const unit = useUnitDetail(entityId, { enabled: (isMasterDataFacet || isUnitDuplicateSheetOpen) && kind === 'unit' })
  const tenant = useTenantDetail(entityId, { enabled: isMasterDataFacet && kind === 'tenant' })
  const contract = useContractDetail(entityId, { enabled: (isMasterDataFacet || isOverview) && kind === 'contract' })

  // Reiter *Geld*, dieselbe Komponente an vier Objektarten (Spec 03, „Reiter
  // Geld — die Vertragsliste"): am Mieter über `tenant_id`, sonst über
  // `subject_id` — der Teilbaum ist ein rekursiver CTE **in** der einen
  // Abfrage, kein zweiter Aufruf. Am Vertrag selbst zeigt der Reiter seit
  // Spec 04a die Sollstellungen (`ChargesList` unten), deshalb fehlt
  // `contract` in der Bedingung unten.
  const isContractListKind = kind === 'property' || kind === 'building' || kind === 'unit' || kind === 'tenant'
  const contractsList = useInfiniteContractsList(kind === 'tenant' ? { tenantId: entityId } : { subjectId: entityId, ...(kind === 'unit' ? { status: 'active' } : {}) }, {
    enabled: isOverview && isContractListKind,
  })

  const openItems = useOpenItems({ scopeId: entityId }, { enabled: isOverview && kind !== 'folder' && kind !== 'tenant' })
  const relatedUnits = useFlatEntities(
    { kind: 'unit', within: entityId },
    { enabled: isOverview && (kind === 'property' || kind === 'building') },
  )
  const relatedUnitsAccessError = relatedUnits.error instanceof ApiError && (relatedUnits.error.isForbidden || relatedUnits.error.isUnauthenticated)
  const hasReadableRelatedUnits = relatedUnits.data !== undefined && !relatedUnitsAccessError
  const relatedUnitItems =
    (hasReadableRelatedUnits ? relatedUnits.data.pages : [])
      .flatMap((page) => page.items)
      .filter((unit, index, items) => items.findIndex((candidate) => candidate.id === unit.id) === index)

  // Sollstellungen eines Vertrags (Spec 04a) — unbedingt sobald `kind ===
  // 'contract'`, nicht erst im Reiter *Geld*: Der Reiter *Stammdaten*
  // braucht dasselbe Ergebnis, um zu entscheiden, ob Miete und Nebenkosten
  // gesperrt sind (`ContractDetail` trägt dafür kein eigenes Feld, siehe
  // `features/billing/api.ts::useContractCharges`).
  const contractCharges = useContractCharges(entityId, { enabled: kind === 'contract' && (isOverview || isMasterDataFacet) })
  const hasCharges = contractCharges.data?.pages.some((page) => page.items.length > 0) ?? false

  // Reiter *Aufgaben* (Spec 06a) — an **jeder** Objektart, anders als der
  // Reiter *Geld*: eine Aufgabe kennt keine Einschränkung nach Art
  // („anlegen an jedem Objekt"). `?scope=` liefert das Objekt **und** seinen
  // Teilbaum (AK-388), derselbe rekursive CTE wie bei der Vertragsliste.
  const tasksList = useInfiniteTicketsList({ scope: entityId }, { enabled: tab === 'aufgaben' })

  // Reiter *Termine* (Spec 06b) — dieselbe Schaltung wie *Aufgaben*: an
  // jeder Objektart, `?scope=` liefert das Objekt **und** seinen Teilbaum
  // (AK-417). Kein Bereich, kein Zähler — ADR 0017.
  const dueDatesList = useInfiniteDueDatesList({ scope: entityId }, { enabled: tab === 'termine' })

  // Reiter *Dokumente* (Spec 09, umbenannt aus *Unterlagen*) — an jeder
  // Objektart, `?scope=` liefert das Objekt **und** seinen Teilbaum
  // (AK-514, „zeigt die Dokumente des Objekts und seiner Nachfahren").
  const documentsList = useInfiniteDocumentsList({ scope: entityId }, { enabled: tab === 'dokumente' })

  const [isBuildingSheetOpen, setIsBuildingSheetOpen] = useState(false)
  const [isUnitSheetOpen, setIsUnitSheetOpen] = useState(false)
  // Spec 46a: „+ Zähler" an Grundstück und Gebäude; die Objektübersicht lädt
  // nichts nach (kein Energie-Block), der Streifen bestätigt das Anlegen.
  const [isMeterSheetOpen, setIsMeterSheetOpen] = useState(false)
  const [meterConfirmation, setMeterConfirmation] = useState<string | null>(null)
  const [isMoveSheetOpen, setIsMoveSheetOpen] = useState(false)
  const [isAccessSheetOpen, setIsAccessSheetOpen] = useState(false)
  const [isContractSheetOpen, setIsContractSheetOpen] = useState(false)
  const [isTenantCreateSheetOpen, setIsTenantCreateSheetOpen] = useState(false)
  // Der zuletzt aus dem Mietersheet zurückgekommene Mieter — wandert als Prop
  // ins offene `ContractCreateSheet` (Spec 03, „Sheet Mieter anlegen": „nach
  // dem Speichern ist der neue Mieter ausgewählt").
  const [pendingTenant, setPendingTenant] = useState<TenantSelection | null>(null)
  // Aussperr-Ausweg (Spec 05b, „Wenn 409 would_lock_out kommt"):
  // `PrivateToggle` (`features/objects`) kennt `features/access` nicht, öffnet
  // dieses Sheet deshalb über `onRequestGrantSelf` statt selbst.
  const [isGrantSelfSheetOpen, setIsGrantSelfSheetOpen] = useState(false)

  // Gilt für Anlegen, Umhängen und Duplizieren gleichermaßen — bei
  // `archived_at != null` sind alle drei Kopfaktionen nicht vorhanden
  // (Spec 02a „Kind anlegen"/Spec 02b „Kopfaktionen der Objektansicht").
  const isNotArchived = head.data?.archived_at === null
  // Elternobjekt des geöffneten Datensatzes — nicht nur fürs Duplizieren
  // (Spec 02b) mehr, seit Spec 05b auch die Rückfrage von `PrivateToggle`
  // nennt es beim Namen. `path` ist der Pfad der sichtbaren Vorfahren; das
  // letzte Glied ist das unmittelbare Elternobjekt, ohne einen weiteren
  // Aufruf (`ObjectHead.path`).
  const parentLabel = head.data?.path.at(-1)?.label ?? ''

  // Für `object-private-grant-self` (Spec 05b): die eigene Person, vorbelegt
  // in `AccessAddGrantSheet`. Noch nicht geladen ist kein Fehler — der Pfad
  // ist ohnehin nur nach einem 409 `would_lock_out` erreichbar, bis dahin hat
  // `useMe` (auch vom Konto-Menü benutzt) längst geantwortet.
  const me = useMe()
  const selfSelection: PersonSelection | undefined =
    me.data === undefined ? undefined : { id: me.data.id, label: me.data.display_name ?? me.data.email }

  useEffect(() => {
    if (tab !== 'geld' || !head.isSuccess) return
    const panel = document.getElementById('object-panel-geld')
    panel?.scrollIntoView({ block: 'start' })
    // AK-754 verlangt Fokus, nicht nur Sichtbarkeit: Wer über die Kachel oder
    // `?tab=geld` herkommt, soll mit der Tastatur im Geldabschnitt weiterlesen.
    // Das Panel trägt dafür `tabindex={-1}`.
    panel?.focus()
  }, [head.isSuccess, tab])

  return (
    <section aria-labelledby="object-heading" data-testid="screen-object">
      <ObjectArchivedNotice notice={archivedNotice} />

      {head.isPending && <SkeletonRows testId="object-head-loading" rows={2} />}

      {head.isError && (
        <ErrorState
          testId="object-head-error"
          retryTestId="object-head-retry"
          error={head.error}
          onRetry={() => {
            void head.refetch()
          }}
        />
      )}

      {head.isSuccess && (
        <>
          <ObjectPath path={head.data.path} />

          {/* Unter 768px eigene Zeile für die Kopfaktionen statt einer
           * festen `--row-h`-Zeile (K7, `design-reviewer`): Mit `object-access`
           * dazu reichen bis zu vier volltextige Knöpfe nicht mehr neben den
           * Titel, ohne ihn auf wenige Zeichen zu stutzen — unabhängig davon,
           * wie viele Aktionen eine Objektart hat, nicht erst ab einer
           * bestimmten Anzahl. `object-access` bleibt dabei ein normaler,
           * sofort klickbarer Knopf (kein ⋯-Menü wie auf iOS): Spec 05a,
           * „Wo", verlangt für Web genau das, und `05a-zugriff.spec.ts` klickt
           * ihn bei 380px direkt an. */}
          {/* `md:min-h-` statt `md:h-` (D-W1, `15-design-reviewer.md`): Der Kopf
           * trägt am Schreibtisch bis zu fünf 44-px-Knöpfe (`--tap-min`) gegen
           * ein 36-px-Band (`--row-h`) — eine feste Höhe schneidet sie an, ein
           * Mindestmaß lässt den Kopf mit den Knöpfen wachsen. Unter 768px ist
           * die Zeile ohnehin `flex-col` (K7 oben) und bleibt unverändert. */}
          <header data-testid="object-header" className="border-border flex flex-col gap-2 border-b px-(--page-x) py-2 md:min-h-(--row-h) md:flex-row md:items-center md:justify-between md:py-0">
            <div className="min-w-0 md:flex-1">
            <h1
              id="object-heading"
              // `folder-label` ist an `kind='folder'` schon vergeben — an
              // `FolderMasterData.tsx`s einzigem Feld (Spec, „Stammdaten eines
              // Ordners", AK-377). Ein zweites Element mit demselben
              // Bezeichner ist kein zweiter Treffer, sondern eine Kollision
              // (W10, `findings.md`); der Kopf trägt hier stattdessen das
              // `object-head-*`-Präfix, das die Lade-/Fehlerzustände dieses
              // Abschnitts schon benutzen. Die übrigen Objektarten behalten
              // ihr `${kind}-label`, dort tritt keine Feldkollision auf.
              // Beim Vertrag (Spec 31, Fachregel 4) trägt der Titel neu
              // `contract-title` — `contract-label` sitzt direkt darunter
              // (= `overview.subheadline` = `label`), damit bestehende
              // Journeys unverändert grün bleiben.
              data-testid={
                head.data.kind.value === 'folder'
                  ? 'object-head-label'
                  : head.data.kind.value === 'contract'
                    ? 'contract-title'
                    : `${head.data.kind.value}-label`
              }
              className="text-page font-(--font-weight-page) truncate"
            >
              {/* `headline` statt `label`: Für ein Grundstück ist `label`
                * „Straße, Ort", und die Übersicht führt den Ort erneut
                * (Spec 14a, Nachtrag D-7). Ohne Projektion bleibt `label`. */}
              {head.data.overview?.headline ?? head.data.label}
            </h1>
            {/* `contract-label` hängt am Kopf, nicht an der Übersicht: Er muss
             * unter jedem Reiter da sein (`03-vertraege.spec.ts` prüft ihn
             * über alle Direktlinks, Spec 31 Fachregel 4; Tor B, G-B1). */}
            {head.data.kind.value === 'contract' && (
              <p data-testid="contract-label" className="text-label text-muted truncate">
                {head.data.overview?.subheadline ?? head.data.label}
              </p>
            )}
            </div>

            {isNotArchived && (
              <div className="flex flex-wrap items-center gap-2 md:shrink-0 md:justify-end">
                {head.data.kind.value === 'property' && (
                  <button
                    type="button"
                    data-testid="building-add"
                    onClick={() => {
                      setIsBuildingSheetOpen(true)
                    }}
                    className={`text-text px-3 ${FIELD_CLASS}`}
                  >
                    + Gebäude
                  </button>
                )}
                {(head.data.kind.value === 'property' || head.data.kind.value === 'building') && (
                  <button
                    type="button"
                    data-testid="unit-add"
                    onClick={() => {
                      setIsUnitSheetOpen(true)
                    }}
                    className={`text-text px-3 ${FIELD_CLASS}`}
                  >
                    + Einheit
                  </button>
                )}
                {(head.data.kind.value === 'property' || head.data.kind.value === 'building') && (
                  <button
                    type="button"
                    data-testid="meter-add"
                    onClick={() => {
                      setIsMeterSheetOpen(true)
                    }}
                    className={`text-text px-3 ${FIELD_CLASS}`}
                  >
                    + Zähler
                  </button>
                )}
                {/* Umhängen — sekundär wie building-add/unit-add, kein
                 * property-move: Ein Grundstück ist immer Wurzel
                 * (Spec 02b, „Kopfaktionen der Objektansicht"). */}
                {(head.data.kind.value === 'building' || head.data.kind.value === 'unit') && (
                  <button
                    type="button"
                    data-testid={`${head.data.kind.value}-move`}
                    onClick={() => {
                      setIsMoveSheetOpen(true)
                    }}
                    className={`text-text px-3 ${FIELD_CLASS}`}
                  >
                    Umhängen
                  </button>
                )}
                {/* Trennt die beiden Sinngruppen im Kopf (Befund B5,
                 * `tasks/todo-web-schale-b.md`): links, **was unter diesem
                 * Objekt entsteht** („+ Gebäude", „+ Einheit", „Umhängen"),
                 * rechts, **was mit dem Objekt selbst geschieht**
                 * (Duplizieren, Zugriff, Privat).
                 *
                 * Eine 1px-Linie, keine Farbe und kein zweiter Knopfstil: Der
                 * Akzent ist für die primäre Aktion reserviert, und die ist
                 * auf *Stammdaten* „Speichern" im Formular darunter
                 * (`design-system` Teil C). Ein zweiter Akzentknopf im Kopf
                 * wären zwei primäre Aktionen auf einem Bildschirm — genau
                 * das, was der Skill unter „Verboten" führt. */}
                <span aria-hidden="true" className="bg-border hidden h-5 w-px shrink-0 md:block" />
                {/* Duplizieren lädt die nötigen Stammdaten erst nach dem
                 * Klick. Die Übersicht bleibt dadurch ohne Fachdetail-GET
                 * verwendbar; Laden/Fehler stehen am jeweiligen Sheetweg. */}
                {head.data.kind.value === 'property' && (
                  <button
                    type="button"
                    data-testid="property-duplicate"
                    onClick={() => {
                      setIsPropertyDuplicateSheetOpen(true)
                    }}
                    className={`text-text px-3 ${FIELD_CLASS}`}
                  >
                    Duplizieren
                  </button>
                )}
                {head.data.kind.value === 'building' && (
                  <button
                    type="button"
                    data-testid="building-duplicate"
                    onClick={() => {
                      setIsBuildingDuplicateSheetOpen(true)
                    }}
                    className={`text-text px-3 ${FIELD_CLASS}`}
                  >
                    Duplizieren
                  </button>
                )}
                {head.data.kind.value === 'unit' && (
                  <button
                    type="button"
                    data-testid="unit-duplicate"
                    onClick={() => {
                      setIsUnitDuplicateSheetOpen(true)
                    }}
                    className={`text-text px-3 ${FIELD_CLASS}`}
                  >
                    Duplizieren
                  </button>
                )}
                {/* Zugriff — an jeder Objektart, kein sechster Reiter
                 * (Spec 05a, „Wo": „eine Kopfaktion object-access … die ein
                 * Sheet öffnet"). Grants hängen an jeder `entities`-Zeile,
                 * nicht nur an den drei Objektarten mit Umhängen/Duplizieren. */}
                <button
                  type="button"
                  data-testid="object-access"
                  onClick={() => {
                    setIsAccessSheetOpen(true)
                  }}
                  className={`text-text px-3 ${FIELD_CLASS}`}
                >
                  Zugriff
                </button>
                {/* Privat schalten — fünfter Knopf, gleichrangig neben
                 * `object-access` (Spec 05b, „Wo": „kein sechster Reiter").
                 * Sichtbar nur bei `can_change_visibility` — der Client
                 * entscheidet nicht selbst, wer umschalten darf (I9). */}
                {head.data.can_change_visibility && (
                  <PrivateToggle
                    entityId={entityId}
                    label={head.data.label}
                    parentLabel={parentLabel}
                    version={head.data.version}
                    visibility={head.data.visibility.value}
                    onRequestGrantSelf={() => {
                      setIsGrantSelfSheetOpen(true)
                    }}
                  />
                )}
              </div>
            )}
          </header>

          {/* Am Objektkopf, unabhängig vom aktiven Reiter — Privatheit ist
           * eine Eigenschaft des Objekts, keine des gerade offenen Reiters
           * (Spec 05b, „Kennzeichnung im Baum": „Am Objektkopf steht … ein
           * Streifen"). Dieselbe Bauform wie `ArchivedBanner`. */}
          {head.data.visibility.value === 'private' && (
            <p
              data-testid="object-private-notice"
              role="status"
              className="border-border bg-bg text-text border-b px-(--page-x) py-3"
            >
              Privat — den Inhalt sieht nur, wer direkt hier oder darunter Zugriff hat.
            </p>
          )}

          {isOverview && (
            <>
              <section data-testid="object-overview" className="flex flex-col gap-4 px-(--page-x) py-4">
                {head.data.overview !== null && head.data.overview !== undefined && (
                  <div className="border-border flex flex-col gap-4 border p-4">
                    <div>
                      <p className="text-label text-muted">{head.data.overview.headline}</p>
                      <p className="text-body text-text">{head.data.overview.subheadline}</p>
                    </div>
                    {/* Genau zwei Kacheln nebeneinander (Typ-A-Referenz). Der
                     * Titelblock stand vorher als drittes Kind im selben
                     * `sm:grid-cols-2` und erzeugte eine Diagonale: Titel |
                     * Offen in Zeile 1, Belegung | leer in Zeile 2 — samt einem
                     * Spaltenstrich, der nichts trennte. */}
                    <div className="grid gap-2 sm:grid-cols-2">
                    {head.data.overview.open_display !== null && (
                      <button
                        type="button"
                        data-testid="object-metric-open"
                        onClick={() => {
                          void navigate({ search: { tab: 'geld' } })
                        }}
                        className="border-border text-left sm:border-r sm:pr-4"
                      >
                        <span className="text-label text-muted">Offen</span>
                        <span className="text-body text-text block">{head.data.overview.open_display}</span>
                        {head.data.overview.open_status !== null && (
                          <span className={'text-label block ' + statusToneClass(head.data.overview.open_status.value)}>
                            {head.data.overview.open_status.label}
                          </span>
                        )}
                      </button>
                    )}
                    {head.data.overview.occupancy_display !== null && (
                      <button
                        type="button"
                        data-testid="object-metric-occupancy"
                        onClick={() => {
                          document.getElementById('object-related-units')?.scrollIntoView({ block: 'start' })
                        }}
                        className="text-left"
                      >
                        <span className="text-label text-muted">Belegt</span>
                        <span className="text-body text-text block">{head.data.overview.occupancy_display}</span>
                        {/* An der Einheit schreibt der Server denselben Text in
                         * `occupancy_display` und `…_status.label`. Dann steht
                         * das Wort doppelt untereinander — hier einmal zeigen. */}
                        {head.data.overview.occupancy_status !== null &&
                          head.data.overview.occupancy_status.label !== head.data.overview.occupancy_display && (
                            <span className="text-label text-muted block">{head.data.overview.occupancy_status.label}</span>
                          )}
                      </button>
                    )}
                    </div>
                  </div>
                )}
                {(head.data.kind.value === 'property' || head.data.kind.value === 'building') && (
                  <section data-testid="object-related-units" className="border-border border-t pt-4">
                    <h2 className="text-section font-(--font-weight-section) text-text mb-2">Einheiten</h2>
                    {relatedUnits.isPending && <SkeletonRows testId="object-related-units-loading" rows={3} />}
                    {(relatedUnitsAccessError || (relatedUnits.isError && !hasReadableRelatedUnits)) && (
                      <ErrorState
                        testId="object-related-units-error"
                        retryTestId="object-related-units-retry"
                        error={relatedUnits.error}
                        onRetry={() => {
                          void relatedUnits.refetch()
                        }}
                      />
                    )}
                    {hasReadableRelatedUnits && relatedUnitItems.length === 0 && (
                      <p data-testid="object-related-units-empty" className="text-muted text-body py-2">
                        Keine sichtbare Einheit.
                      </p>
                    )}
                    {hasReadableRelatedUnits && relatedUnitItems.length > 0 && (
                      <ul className="border-border border-t">
                        {relatedUnitItems.map((unit) => (
                          <li
                            key={unit.id}
                            data-testid={'object-related-unit-' + unit.id}
                            className="border-border border-b last:border-b-0"
                          >
                            <Link
                              to="/objekt/$entityId"
                              params={{ entityId: unit.id }}
                              search={{ tab: undefined }}
                              className="hover:bg-bg flex min-h-(--row-h) items-center justify-between gap-3 px-3 py-2"
                            >
                              <span className="min-w-0">
                                <span className="text-body text-text block truncate">{unit.overview?.headline ?? unit.label}</span>
                                {/* D-6: In der Einheiten-Sektion ist der Vorfahre
                                  * immer das geöffnete Objekt — die Kette stand
                                  * dreimal untereinander (Breadcrumb, Untertitel,
                                  * und unter jeder Zeile). Gleicht sie der Kette
                                  * des Kopfes, sagt sie hier nichts. */}
                                {unit.overview?.subheadline !== undefined &&
                                  unit.overview.subheadline !== head.data.overview?.subheadline && (
                                  <span className="text-label text-muted block truncate">{unit.overview.subheadline}</span>
                                )}
                              </span>
                              {unit.overview?.open_display !== null && unit.overview?.open_display !== undefined && (
                                <span data-numeric className="text-body text-text shrink-0 text-right">
                                  {unit.overview.open_display}
                                  {unit.overview.open_status !== null && (
                                    <span className={'text-label block ' + statusToneClass(unit.overview.open_status.value)}>
                                      {unit.overview.open_status.label}
                                    </span>
                                  )}
                                </span>
                              )}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                    {hasReadableRelatedUnits && relatedUnits.hasNextPage && (
                      <button
                        type="button"
                        data-testid="object-related-units-more"
                        disabled={relatedUnits.isFetchingNextPage}
                        onClick={() => {
                          void relatedUnits.fetchNextPage()
                        }}
                        className="border-border text-text mt-2 border px-3"
                      >
                        Mehr laden
                      </button>
                    )}
                  </section>
                )}
                {head.data.kind.value === 'contract' && (
                  <ContractMasterData
                    entityId={entityId}
                    canEdit={head.data.can_edit}
                    canUnarchive={head.data.can_unarchive}
                    archivedById={head.data.archived_by_id}
                    archivedByLabel={head.data.archived_by_label}
                    query={contract}
                    hasCharges={hasCharges}
                  />
                )}
                {head.data.kind.value !== 'folder' && (
                  <section id="object-panel-geld" tabIndex={-1} data-testid="object-panel-geld" className="border-border border-t pt-4">
                    <h2 className="text-section font-(--font-weight-section) text-text mb-2">Geld</h2>
                    {isContractListKind ? (
                      <>
                        <ContractList
                          query={contractsList}
                          currentEntityId={entityId}
                          canAdd={head.data.kind.value !== 'tenant' && isNotArchived}
                          onAddClick={() => {
                            setIsContractSheetOpen(true)
                          }}
                          onOpenContract={(nextContractId) => {
                            void navigate({ search: { tab, contract: nextContractId } })
                          }}
                        />
                        {head.data.kind.value === 'tenant' && <TenantLedger tenantId={entityId} />}
                      </>
                    ) : head.data.kind.value === 'contract' ? (
                      <ChargesList query={contractCharges} contractId={entityId} />
                    ) : null}
                    {head.data.kind.value !== 'contract' && head.data.kind.value !== 'tenant' && (
                      <OpenItemsList
                        query={openItems}
                        listTestId="object-open-items"
                        loadingTestId="object-open-items-loading"
                        errorTestId="object-open-items-error"
                        retryTestId="object-open-items-retry"
                        emptyTestId="object-open-items-empty"
                        emptyTitle="Keine offenen Posten."
                        rowTestId={(chargeId) => `object-open-item-${chargeId}`}
                        overdueTestId={(chargeId) => `object-open-item-overdue-${chargeId}`}
                        arrearsTestId={(chargeId) => `object-open-item-arrears-${chargeId}`}
                        onOpenContract={(nextContractId) => {
                          void navigate({ search: { tab, contract: nextContractId } })
                        }}
                      />
                    )}
                  </section>
                )}
              </section>
              {/* `print:hidden` — die Facets gehören zur Hülle, nicht zum
               * Ausdruck (Spec 04c, „Druckansicht"), dieselbe Regel wie zuvor
               * an `object-tabs`. */}
              <nav aria-label="Facets" data-testid="object-facets" className="border-border border-t print:hidden">
                {FACETS.map((facet) => (
                  <button
                    key={facet.id}
                    type="button"
                    data-testid={'object-facet-' + facet.id}
                    onClick={() => {
                      void navigate({ search: { tab: facet.id } })
                    }}
                    className="border-border text-body text-text hover:bg-bg flex min-h-(--row-h) w-full items-center justify-between border-b px-(--page-x) text-left"
                  >
                    <span className="flex-1">{facet.label}</span>
                    {/* Feste Zählerspalte: `justify-between` mit drei Kindern
                     * setzt den Zähler in die Mitte des Restraums, seine
                     * Position hing an der Labelbreite (gemessen 11 px Drift
                     * bei drei identischen Ziffern untereinander). */}
                    {facet.id !== 'stammdaten' && head.data.counts !== null && head.data.counts !== undefined && (
                      <span data-numeric data-testid={'object-facet-' + facet.id + '-count'} className="text-label text-muted w-8 text-right">
                        {facet.id === 'dokumente'
                          ? head.data.counts.documents
                          : facet.id === 'aufgaben'
                            ? head.data.counts.tasks
                            : head.data.counts.due_dates}
                      </span>
                    )}
                    <span aria-hidden="true">›</span>
                  </button>
                ))}
              </nav>
            </>
          )}

          {!isOverview && (tab === 'stammdaten' ? (
            <>
              {head.data.kind.value === 'property' && (
                <PropertyMasterData
                  entityId={entityId}
                  canEdit={head.data.can_edit}
                  canUnarchive={head.data.can_unarchive}
                  archivedById={head.data.archived_by_id}
                  archivedByLabel={head.data.archived_by_label}
                  query={property}
                />
              )}
              {head.data.kind.value === 'building' && (
                <BuildingMasterData
                  entityId={entityId}
                  canEdit={head.data.can_edit}
                  canUnarchive={head.data.can_unarchive}
                  archivedById={head.data.archived_by_id}
                  archivedByLabel={head.data.archived_by_label}
                  query={building}
                />
              )}
              {head.data.kind.value === 'unit' && (
                <UnitMasterData
                  entityId={entityId}
                  canEdit={head.data.can_edit}
                  canUnarchive={head.data.can_unarchive}
                  archivedById={head.data.archived_by_id}
                  archivedByLabel={head.data.archived_by_label}
                  query={unit}
                />
              )}
              {head.data.kind.value === 'tenant' && (
                <TenantMasterData
                  entityId={entityId}
                  canEdit={head.data.can_edit}
                  canUnarchive={head.data.can_unarchive}
                  archivedById={head.data.archived_by_id}
                  archivedByLabel={head.data.archived_by_label}
                  query={tenant}
                />
              )}
              {head.data.kind.value === 'contract' && (
                <ContractMasterData
                  entityId={entityId}
                  canEdit={head.data.can_edit}
                  canUnarchive={head.data.can_unarchive}
                  archivedById={head.data.archived_by_id}
                  archivedByLabel={head.data.archived_by_label}
                  query={contract}
                  hasCharges={hasCharges}
                />
              )}
              {/* Ein Ordner hat keine Fachtabelle — genau ein Feld, der Name
               * (Spec 05b, „Stammdaten eines Ordners"). */}
              {head.data.kind.value === 'folder' && (
                <FolderMasterData
                  entityId={entityId}
                  canEdit={head.data.can_edit}
                  label={head.data.label}
                  version={head.data.version}
                />
              )}
            </>
          ) : tab === 'aufgaben' ? (
            // `object-panel-aufgaben` (AK-399) trägt hier die Hülle, nicht
            // `TaskList` selbst — dieselbe Komponente steht auch im Bereich
            // *Aufgaben* (`routes/Aufgaben.tsx`), das kein `object-panel-*`
            // kennt.
            <div data-testid="object-panel-aufgaben">
              <TaskList
                query={tasksList}
                subjectId={entityId}
                subjectLabel={head.data.label}
                canAdd={isNotArchived}
                emptyTitle="Nichts zu tun."
                emptyHint="Was hier ansteht, wird als Aufgabe festgehalten."
              />
            </div>
          ) : tab === 'termine' ? (
            // `object-panel-termine` (AK-422) trägt hier die Hülle, dieselbe
            // Aufteilung wie `object-panel-aufgaben` — es gibt keinen
            // Bereich *Termine*, der eine zweite Stelle bräuchte (ADR 0017).
            <div data-testid="object-panel-termine">
              <DueDateList
                query={dueDatesList}
                subjectId={entityId}
                subjectLabel={head.data.label}
                canAdd={isNotArchived}
                emptyTitle="Keine Termine."
                emptyHint="Wiederkehrende Prüfungen und Fristen werden hier eingetragen — acht Vorlagen sind hinterlegt."
              />
            </div>
          ) : (
            // `object-panel-dokumente` (AK-514) trägt hier die Hülle,
            // dieselbe Aufteilung wie `object-panel-aufgaben`/`-termine` —
            // `DocumentList` selbst kennt Bereich und Reiter nicht
            // auseinander.
            <div data-testid="object-panel-dokumente">
              <DocumentList
                query={documentsList}
                fixedSubjectId={entityId}
                fixedSubjectLabel={head.data.label}
                emptyTitle="Hier liegt noch nichts."
                emptyHint="Rechnungen, Verträge und Post gehören an das Objekt, um das es geht."
              />
            </div>
          ))}

          {head.data.kind.value === 'property' && (
            <BuildingSheet
              open={isBuildingSheetOpen}
              onOpenChange={setIsBuildingSheetOpen}
              parentId={entityId}
              parentLabel={head.data.label}
            />
          )}
          {(head.data.kind.value === 'property' || head.data.kind.value === 'building') && (
            <UnitSheet
              open={isUnitSheetOpen}
              onOpenChange={setIsUnitSheetOpen}
              parentId={entityId}
              parentLabel={head.data.label}
            />
          )}

          {(head.data.kind.value === 'property' || head.data.kind.value === 'building') && (
            <>
              <MeterCreateSheet
                open={isMeterSheetOpen}
                onOpenChange={setIsMeterSheetOpen}
                fixedHouse={{ id: entityId, label: head.data.label }}
                onCreated={() => {
                  setMeterConfirmation('Zähler angelegt.')
                }}
              />
              <ConfirmationStrip
                message={meterConfirmation}
                onDismiss={() => {
                  setMeterConfirmation(null)
                }}
                testId="meter-create-confirmation"
              />
            </>
          )}

          {(head.data.kind.value === 'building' || head.data.kind.value === 'unit') && (
            <MoveSheet
              open={isMoveSheetOpen}
              onOpenChange={setIsMoveSheetOpen}
              entityId={entityId}
              label={head.data.label}
              version={head.data.version}
            />
          )}

          {head.data.kind.value === 'property' && property.data !== undefined && (
            <PropertySheet
              open={isPropertyDuplicateSheetOpen}
              onOpenChange={setIsPropertyDuplicateSheetOpen}
              duplicateFrom={property.data}
            />
          )}
          {head.data.kind.value === 'property' && isPropertyDuplicateSheetOpen && property.isPending && (
            <SkeletonRows testId="property-duplicate-loading" rows={8} />
          )}
          {head.data.kind.value === 'property' && isPropertyDuplicateSheetOpen && property.isError && (
            <ErrorState testId="property-duplicate-error" retryTestId="property-duplicate-retry" error={property.error} onRetry={() => { void property.refetch() }} />
          )}
          {head.data.kind.value === 'building' && building.data?.parent_id != null && (
            <BuildingSheet
              open={isBuildingDuplicateSheetOpen}
              onOpenChange={setIsBuildingDuplicateSheetOpen}
              parentId={building.data.parent_id}
              parentLabel={parentLabel}
              duplicateFrom={building.data}
            />
          )}
          {head.data.kind.value === 'building' && isBuildingDuplicateSheetOpen && building.isPending && (
            <SkeletonRows testId="building-duplicate-loading" rows={5} />
          )}
          {head.data.kind.value === 'building' && isBuildingDuplicateSheetOpen && building.isError && (
            <ErrorState testId="building-duplicate-error" retryTestId="building-duplicate-retry" error={building.error} onRetry={() => { void building.refetch() }} />
          )}
          {head.data.kind.value === 'unit' && unit.data?.parent_id != null && (
            <UnitSheet
              open={isUnitDuplicateSheetOpen}
              onOpenChange={setIsUnitDuplicateSheetOpen}
              parentId={unit.data.parent_id}
              parentLabel={parentLabel}
              duplicateFrom={unit.data}
            />
          )}
          {head.data.kind.value === 'unit' && isUnitDuplicateSheetOpen && unit.isPending && (
            <SkeletonRows testId="unit-duplicate-loading" rows={5} />
          )}
          {head.data.kind.value === 'unit' && isUnitDuplicateSheetOpen && unit.isError && (
            <ErrorState testId="unit-duplicate-error" retryTestId="unit-duplicate-retry" error={unit.error} onRetry={() => { void unit.refetch() }} />
          )}

          {/* `contract-add` sitzt nur an den drei Objektarten, nie am
           * Mieter (Spec 03, „Warum der Knopf im Reiter sitzt"). */}
          {(head.data.kind.value === 'property' ||
            head.data.kind.value === 'building' ||
            head.data.kind.value === 'unit') && (
            <ContractCreateSheet
              open={isContractSheetOpen}
              onOpenChange={setIsContractSheetOpen}
              subjectId={entityId}
              subjectLabel={head.data.label}
              onRequestNewTenant={() => {
                setIsTenantCreateSheetOpen(true)
              }}
              pendingTenant={pendingTenant}
              onCreated={(contractId) => {
                void navigate({
                  to: '/objekt/$entityId',
                  params: { entityId: contractId },
                  search: { tab: undefined },
                })
              }}
            />
          )}

          <AccessSheet
            open={isAccessSheetOpen}
            onOpenChange={setIsAccessSheetOpen}
            entityId={entityId}
            entityLabel={head.data.label}
          />

          {/* Aussperr-Ausweg von `PrivateToggle` (Spec 05b, „Wenn 409
           * would_lock_out kommt"): dasselbe Sheet wie „Zugriff geben", mit
           * der eigenen Person vorbelegt und gesperrt — keine Suche nötig,
           * um sich selbst Zugriff auf genau dieses Objekt zu geben.
           * „Person einladen" wäre hier ein Widerspruch (die Person steht
           * schon fest), bleibt aber erreichbar, sollte doch jemand über
           * `PersonPicker`s „Ändern" wechseln wollen. */}
          {/* `presetPerson` per Spread statt als Prop mit `undefined`:
           * `AccessAddGrantSheetProps.presetPerson` ist optional
           * (`presetPerson?: PersonSelection`), nicht `| undefined` — unter
           * `exactOptionalPropertyTypes: true` (TS2375) muss die Eigenschaft
           * fehlen, nicht auf `undefined` stehen. Vor dem Laden von `useMe`
           * bleibt das Feld dadurch unbelegt statt explizit leer — deckt sich
           * mit `EMPTY_FORM.person: null` in `AccessAddGrantSheet.tsx`, dem
           * Fallback bei fehlendem `presetPerson`. */}
          <AccessAddGrantSheet
            open={isGrantSelfSheetOpen}
            onOpenChange={setIsGrantSelfSheetOpen}
            entityId={entityId}
            entityLabel={head.data.label}
            excludeIds={[]}
            {...(selfSelection !== undefined ? { presetPerson: selfSelection } : {})}
            onRequestInvite={() => {
              setIsGrantSelfSheetOpen(false)
              setIsAccessSheetOpen(true)
            }}
          />

        </>
      )}

      {/* Außerhalb von `head.isSuccess` gemountet: Ein Mieter hängt an keinem
       * Elternobjekt (Spec 03, „Ein Mieter ist eine Wurzel") — das Sheet
       * braucht `head.data` nicht und darf deshalb unbedingt stehen. */}
      <TenantCreateSheet
        open={isTenantCreateSheetOpen}
        onOpenChange={setIsTenantCreateSheetOpen}
        onCreated={(created) => {
          setPendingTenant({ id: created.id, label: created.label, salutation: created.salutation })
        }}
      />
    </section>
  )
}
